import lottie, { AnimationItem } from 'lottie-web';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import { View, ViewStyle } from 'react-native';

import { LottieViewProps } from './types';

interface AnimationProps extends LottieViewProps {
  rendererSettings?: object;
}

export interface LottieViewRef {
  play: (...frames: [number, number]) => void;
  reset: () => void;
}

const LottieView = forwardRef<LottieViewRef, AnimationProps>((props, ref) => {
  const animationDivRef = useRef<HTMLDivElement>(null);
  const anim = useRef<AnimationItem | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (anim.current) {
        anim.current.destroy();
      }

      let animationData = props.source as any;

      if (typeof props.source === 'object' && 'uri' in props.source) {
        const response = await fetch(props.source.uri);

        if (!response.ok) {
          return;
        }

        animationData = await response.json();

        if (cancelled) {
          return;
        }
      }

      anim.current = lottie.loadAnimation({
        container: animationDivRef.current as Element,
        animationData,
        renderer: 'svg',
        loop: props.loop || false,
        autoplay: props.autoPlay,
        rendererSettings: props.rendererSettings || {},
      });

      if (props.onAnimationFinish) {
        anim.current.addEventListener('complete', () =>
          props.onAnimationFinish?.(false)
        );
      }
    }

    load();

    return () => {
      cancelled = true;

      if (anim.current) {
        anim.current.destroy();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.source, props.loop, props.autoPlay, props.rendererSettings]);

  useEffect(() => {
    const progress: any = props.progress;

    if (progress && progress._listeners) {
      const listener = (progress: { value: number }) => {
        const { value } = progress;

        if (anim.current) {
          const frame = value / (1 / anim.current.getDuration(true));
          anim.current.goToAndStop(frame, true);
        }
      };

      progress.addListener(listener);

      return () => {
        progress.removeListener(listener);
      };
    }
  }, [props.progress]);

  useImperativeHandle(ref, () => ({
    play: (...frames: [number, number]) => {
      if (anim.current) {
        anim.current.playSegments(frames, true);
      }
    },
    reset: () => {
      if (anim.current) {
        anim.current.stop();
      }
    },
  }));

  return (
    <View style={props.style as ViewStyle}>
      <div ref={animationDivRef} style={{ width: '100%', height: '100%' }} />
    </View>
  );
});

export default LottieView;
